import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { paymentsService } from '../../services/api';

const AddPaymentModal = ({ booking, onClose, onPaymentAdded }) => {
  const [paymentData, setPaymentData] = useState({
    amount: booking.finalAmount || booking.totalPrice || 0,
    paymentMethod: 'cash',
    bankingMethod: 'netbanking', // For bank payments
    paymentDate: new Date().toISOString().split('T')[0],
    description: '',
  });
  const [loading, setLoading] = useState(false);

  const paymentMethods = [
    { id: 'cash', label: 'Cash' },
    { id: 'bank', label: 'Bank Transfer' },
  ];

  const bankingMethods = [
    { id: 'netbanking', label: 'Net Banking' },
    { id: 'credit_card', label: 'Credit Card' },
    { id: 'debit_card', label: 'Debit Card' },
    { id: 'upi', label: 'UPI' },
    { id: 'razorpay', label: 'Razorpay' },
  ];

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentData.amount || paymentData.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!paymentData.paymentDate) {
      toast.error('Please select a payment date');
      return;
    }

    try {
      setLoading(true);
      
      const paymentPayload = {
        bookingId: booking.id,
        amount: parseFloat(paymentData.amount),
        method: paymentData.paymentMethod === 'cash' ? 'cash' : paymentData.bankingMethod,
        paymentDate: paymentData.paymentDate,
        description: paymentData.description.trim() || `${paymentData.paymentMethod === 'cash' ? 'Cash' : paymentData.bankingMethod} payment for booking ${booking.id}`,
        adminCreated: true, // Flag to indicate this was created by admin
      };

      // Call the API service
      const result = await paymentsService.createManualPayment(paymentPayload);

      if (result.success) {
        toast.success('Payment recorded successfully');
        onPaymentAdded(result.data);
        onClose();
      } else {
        throw new Error(result.error || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '20px',
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: '#E9DFCE',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 75px rgba(0, 0, 0, 0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E3DFD3',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '700', color: '#24332A' }}>
              Add Payment
            </h2>
            <p style={{ margin: 0, color: '#5F6B61', fontSize: '14px' }}>
              Booking: {booking.id.slice(-8)} • {booking.user?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#5F6B61',
              padding: '6px',
              borderRadius: '6px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Payment Method Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
              Payment Method
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => handleInputChange('paymentMethod', method.id)}
                  style={{
                    padding: '12px',
                    border: paymentData.paymentMethod === method.id ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
                    borderRadius: '8px',
                    backgroundColor: paymentData.paymentMethod === method.id ? '#F0F3EE' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Banking Method Selection (only for bank payments) */}
          {paymentData.paymentMethod === 'bank' && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
                Banking Method
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                {bankingMethods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => handleInputChange('bankingMethod', method.id)}
                    style={{
                      padding: '8px 12px',
                      border: paymentData.bankingMethod === method.id ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
                      borderRadius: '6px',
                      backgroundColor: paymentData.bankingMethod === method.id ? '#F0F3EE' : 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                    }}
                  >
                    {method.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Amount */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
              Amount (₹)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={paymentData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
              placeholder="Enter payment amount"
              required
            />
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#5F6B61' }}>
              Booking total: ₹{booking.finalAmount || booking.totalPrice || 0}
            </p>
          </div>

          {/* Payment Date */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
              Payment Date
            </label>
            <input
              type="date"
              value={paymentData.paymentDate}
              onChange={(e) => handleInputChange('paymentDate', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
              required
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
              Description (Optional)
            </label>
            <textarea
              value={paymentData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
                minHeight: '80px',
                resize: 'vertical',
              }}
              placeholder="Enter payment description or notes"
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 20px',
                border: '1px solid #E3DFD3',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#36433B',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: loading ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {loading && <div style={{ width: '16px', height: '16px', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />}
              {loading ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AddPaymentModal;
